// React / Next
import { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

// Hooks
import { useTrackEvent } from '../../hooks/useTrackEvent';

// Utils
import { cn } from '../../utils/cn';

// Types
type LetsTalkSectionProps = {
  /** Main heading text */
  title?: string;
  /** Description paragraph text */
  description?: string;
  /** CTA button text */
  ctaText?: string;
  /** CTA link URL. Internal paths start with "/", external URLs start with "https://" */
  ctaLink?: string;
  /** Optional className for additional styling */
  className?: string;
  /** Optional children to render instead of default content */
  children?: ReactNode;
};

/**
 * LetsTalkSection - Reusable CTA section for contact/demo requests
 * Features a full-width container with centered content aligned to the left
 */
export const LetsTalkSection = ({
  title = "Let's Talk.",
  description = "We'd love to learn about your tech landscape and transformation priorities, and show you how Archie works with your existing systems.",
  ctaText = 'Contact Sales',
  ctaLink = '/contact-sales',
  className,
  children,
}: LetsTalkSectionProps) => {
  const trackEvent = useTrackEvent();

  return (
    <section
      className={cn(
        'w-full',
        'py-[var(--spacing-96)]',
        'max-md:py-[var(--spacing-64)]',
        className
      )}
    >
      {/* Wrapper - centers content horizontally within section */}
      <div className="flex justify-center px-[var(--spacing-24)] max-md:px-[var(--spacing-16)]">
        {/* Content container - left-aligned content */}
        <div className="flex flex-col items-start gap-[var(--spacing-24)]">
          {children ? (
            children
          ) : (
            <>
              {/* Blue decorative line */}
              <div
                className="w-[88px] h-[4px] bg-blue-1000 rounded-full"
                aria-hidden="true"
              />

              {/* Title */}
              <h2 className="font-heading text-[80px] leading-[96px] tracking-[-2.4px] font-light text-blue-1000 max-md:text-[56px] max-md:leading-[64px]">
                {title}
              </h2>

              {/* Description */}
              <p className="font-body text-[16px] leading-[24px] text-blue-1000 max-w-[496px]">
                {description}
              </p>

              {/* CTA Button */}
              {(() => {
                const resolvedLink = ctaLink ?? '/contact-sales';
                const isExternal = resolvedLink.startsWith('http');
                const handleClick = () =>
                  trackEvent('cta_clicked', {
                    cta_text: ctaText,
                    cta_location: 'lets_talk_section',
                    cta_href: resolvedLink,
                  });
                const buttonEl = (
                  <button className="inline-flex items-center justify-center gap-[var(--spacing-8)] px-[var(--spacing-16)] py-[var(--spacing-12)] rounded-[var(--radius-8)] font-body text-[16px] leading-[24px] font-normal transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 bg-orange-800 text-white hover:bg-orange-1000 focus:ring-orange-800">
                    {ctaText}
                  </button>
                );
                return isExternal ? (
                  <a href={resolvedLink} target="_blank" rel="noopener noreferrer" onClick={handleClick}>
                    {buttonEl}
                  </a>
                ) : (
                  <Link to={resolvedLink} onClick={handleClick}>
                    {buttonEl}
                  </Link>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

